import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getFormattedContractRestrictionDates } from 'utils/reservation';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { formatDateTimeStampAndReturnDateTimeObj } from 'utils/date';
import { showEveryDayLowRatesBanner } from 'utils/banners';
import { i18n } from 'utils/i18n';
import { dtm } from 'utils/analytics';
import { config } from 'utils/config';
import { ANALYTICS, GMI_SERVICE_PATHS, RESERVATIONS, WINDOW_OBJECT_KEYS } from 'constants';
import Button from 'components/Button';
import Anchor from 'components/Anchor';
import GenericNotification from 'components/GenericNotification';
import LocationDateTimeFieldGroup from 'components/BookingWidget/LocationDateTimeFieldGroup';
import LocationDateTimeFormSpy from 'components/BookingWidget/LocationDateTimeFieldGroup/LocationDateTimeFormSpy';
import Form from 'components/Form/Form';
import ServiceErrors from 'components/ServiceErrors';
import ProgressBarDrawerWrapper from '../ProgressBarDrawerWrapper';

const { EXCHANGE_TYPE_PICKUP, EXCHANGE_TYPE_RETURN } = RESERVATIONS;

const validate = (values) => {
  const errors = {};

  if (!values.pickupDate) {
    errors.pickupDate = i18n('bw_pickup_date_submit_error');
  }
  if (!values.pickupTime) {
    errors.pickupTime = i18n('bw_pickup_time_submit_error');
  }
  if (!values.returnDate) {
    errors.returnDate = i18n('bw_return_date_submit_error');
  }
  if (!values.returnTime) {
    errors.returnTime = i18n('bw_return_time_submit_error');
  }

  return errors;
};

const ProgressBarDatesTimesDrawer = (props) => {
  const {
    values,
    pickupLocationId,
    pickupDateValue,
    pickupEligibility,
    returnLocationId,
    returnDateValue,
    returnEligibility,
    one_way_rental,
    label,
    hasSelectedVehicle,
    contractDetails,
    isLastMinuteOrPlanAheadSpecials,
    onSubmit,
    onDateChange,
    onTimeChange,
    cleanMessages,
    adjustLocationId,
    dispatchSetUpdatedSoldOutDateTime,
    setIsGmiUIOneWayRental,
    setLocation,
    pickupLocation,
    returnLocation,
    dateTimeInitiateRequestValues,
    initiateLocationModifiedSelector,
    onCloseDrawer,
    isPlanAheadSpecials,
    clearDate,
  } = props;
  const { pickupDate, pickupTime, returnDate, returnTime } = values || {};

  const [didInitiate, setDidInitiate] = useState(false);
  const [restrictedDate, setRestrictedDate] = useState(false);
  const initiateRef = useRef(false);

  // LMS and Plan Ahead Specials Variables
  const contractRestrictions = contractDetails?.restrictions;

  const formattedContractRestrictions =
    contractRestrictions && getFormattedContractRestrictionDates(contractRestrictions, isPlanAheadSpecials);

  // Show everday low rate banner according to the CID restrictions
  const shouldDisplayEveryDayLowRatesBanner = (type, date) => {
    const restrictedDateValue = showEveryDayLowRatesBanner(
      type,
      date,
      pickupDate,
      returnDate,
      formattedContractRestrictions
    );
    setRestrictedDate(restrictedDateValue);
  };

  const handleDateChange = (exchangeType) => (date) => {
    const formattedDate = utils.getDateTimeObjFromTs(date).format('YYYY-MM-DD');
    let expectedExchangeType = exchangeType;

    if (exchangeType === EXCHANGE_TYPE_PICKUP && utils.getDateTimeObjFromTs(returnDate).isBefore(formattedDate)) {
      clearDate(EXCHANGE_TYPE_RETURN);
    }

    if (exchangeType === EXCHANGE_TYPE_RETURN && utils.getDateTimeObjFromTs(pickupDate).isAfter(formattedDate)) {
      expectedExchangeType = EXCHANGE_TYPE_PICKUP;
      returnDate && clearDate(EXCHANGE_TYPE_RETURN);
    }

    contractRestrictions &&
      shouldDisplayEveryDayLowRatesBanner(exchangeType, formatDateTimeStampAndReturnDateTimeObj(date));

    onDateChange(formattedDate, expectedExchangeType);
    cleanMessages();
  };

  const handleTimeChange = (exchangeType) => (time) => {
    onTimeChange(time, exchangeType);
  };

  const handleOnSubmit = () => {
    setIsGmiUIOneWayRental(one_way_rental);
    onSubmit().then((response) => {
      if (!response.messages) {
        setDidInitiate(true);
      }
    });

    !!adjustLocationId && dispatchSetUpdatedSoldOutDateTime(true);
  };

  // update message for assistive screen reader users after state is updated
  useEffect(() => {
    if (didInitiate) {
      const responseText = `${i18n('rental_details_dates_times')} ${i18n('common_updated')}`;
      onCloseDrawer(responseText);
      initiateRef.current = true; // updating ref to match the state
    }
  }, [didInitiate]);

  useEffect(() => {
    let restrictedDateValue = false;

    // Show Everyday low rate banner if pickupDate or returnDate are outside the formattedContractRestrictions on first render
    const restrictedPickUpDate = showEveryDayLowRatesBanner(
      EXCHANGE_TYPE_PICKUP,
      null,
      pickupDate,
      returnDate,
      formattedContractRestrictions
    );
    const restrictedReturnDate = showEveryDayLowRatesBanner(
      EXCHANGE_TYPE_RETURN,
      null,
      pickupDate,
      returnDate,
      formattedContractRestrictions
    );
    restrictedDateValue = restrictedPickUpDate || restrictedReturnDate;

    setRestrictedDate(restrictedDateValue);

    if (dateTimeInitiateRequestValues && initiateLocationModifiedSelector) {
      setLocation(pickupLocation, EXCHANGE_TYPE_PICKUP);
      setLocation(returnLocation, EXCHANGE_TYPE_RETURN);
      onDateChange(pickupDate, EXCHANGE_TYPE_PICKUP);
      onTimeChange(pickupTime, EXCHANGE_TYPE_PICKUP);
      onDateChange(returnDate, EXCHANGE_TYPE_RETURN);
      onTimeChange(returnTime, EXCHANGE_TYPE_RETURN);
    }
  }, []);

  useEffect(
    () =>
      // reset gmi > ui, to match what's in gbo, on unmount.
      () => {
        if (!didInitiate && !initiateRef.current) {
          onDateChange(pickupDate, EXCHANGE_TYPE_PICKUP);
          onTimeChange(pickupTime, EXCHANGE_TYPE_PICKUP);
          onDateChange(returnDate, EXCHANGE_TYPE_RETURN);
          onTimeChange(returnTime, EXCHANGE_TYPE_RETURN);
        }
      },
    []
  );

  const returnHomeAnchor = (
    <Anchor
      href={config.getRedirectUrl(WINDOW_OBJECT_KEYS.HOME_PAGE_URL)}
      className='link link--text'
      data-dtm-track={dtm(ANALYTICS.PROGRESS_BAR, ANALYTICS.UI_ALERT, ANALYTICS.RETURN_HOME)}
    >
      {i18n('special_rates_not_available_banner_link_label')}
    </Anchor>
  );

  return (
    <ProgressBarDrawerWrapper>
      <div className='progress-bar__drawer'>
        <h2 className='progress-bar__drawer-title'>{i18n(label)}</h2>
        <ServiceErrors statePath={GMI_SERVICE_PATHS.RESERVATIONS_INITIATE} />
        {contractDetails && isLastMinuteOrPlanAheadSpecials && restrictedDate && (
          <GenericNotification
            messageClass='generic-notification__notifications-on'
            title={i18n('special_rates_not_available_banner_title')}
            message={i18n('special_rates_not_available_banner_message', [returnHomeAnchor], {
              jsx: true,
            })}
            dtmTrack={dtm(ANALYTICS.PROGRESS_BAR, ANALYTICS.UI_ALERT, ANALYTICS.INVALID_DATES)}
          />
        )}
        <Form onSubmit={handleOnSubmit} initialValues={values} validate={validate}>
          {({ form, handleSubmit }) => (
            <form className='progress-bar__dates-times-form' onSubmit={handleSubmit} autoComplete='off'>
              <div className='progress-bar__dates-times-fields'>
                <LocationDateTimeFieldGroup
                  pickup_location_id={pickupLocationId}
                  return_location_id={returnLocationId}
                  pickupDate={pickupDate}
                  pickupDateValue={pickupDateValue}
                  pickupEligibility={pickupEligibility}
                  pickupTime={pickupTime}
                  returnDate={returnDate}
                  returnDateValue={returnDateValue}
                  returnEligibility={returnEligibility}
                  returnTime={returnTime}
                  one_way_rental={one_way_rental}
                  onDateChangeReturn={handleDateChange(EXCHANGE_TYPE_RETURN)}
                  onTimeChangeReturn={handleTimeChange(EXCHANGE_TYPE_RETURN)}
                  onDateChangePickUp={handleDateChange(EXCHANGE_TYPE_PICKUP)}
                  onTimeChangePickUp={handleTimeChange(EXCHANGE_TYPE_PICKUP)}
                  contractRestrictions={isLastMinuteOrPlanAheadSpecials && formattedContractRestrictions}
                />
              </div>
              <Button
                data-dtm-track={dtm(ANALYTICS.UI_BUTTON, ANALYTICS.PROGRESS_BAR, 'update_date_time')}
                type='submit'
                ariaText={`${i18n('progress_bar_submit_value')} ${i18n(label)}`}
                className='progress-bar__drawer-submit'
              >
                {i18n('progress_bar_submit_value')}
              </Button>
              <LocationDateTimeFormSpy form={form} />
            </form>
          )}
        </Form>
        {hasSelectedVehicle && (
          <p className='progress-bar__drawer-help-text'>{i18n('progress_bar_datestimes_help_text')}</p>
        )}
      </div>
    </ProgressBarDrawerWrapper>
  );
};

export default ProgressBarDatesTimesDrawer;

ProgressBarDatesTimesDrawer.propTypes = {
  values: PropTypes.object,
  pickupEligibility: PropTypes.object,
  returnEligibility: PropTypes.object,
  onDateChange: PropTypes.func,
  onTimeChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onUnmount: PropTypes.func,
  adjustLocationId: PropTypes.string,
  dispatchSetUpdatedSoldOutDateTime: PropTypes.func,
};
